import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import Heading from "../../../shared_components/Heading/Heading";
import PaginationGroup from "../../../shared_components/PaginationGroup/PaginationGroup";
import UserData from "../../../services/UserData";
import IngestedDocumentsService from "../../../services/IngestedDocumentsService";
import NotFound from "../../NotFound/NotFound";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import style from "./IngestedDocuments.module.css";

const CreatedAt = ({ value }) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  const formattedDate = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return <time dateTime={value} title={value}>{formattedDate}, {date.toLocaleTimeString()}</time>;
};

const IngestedDocuments = () => {
  const userData = useContext(UserData);
  const location = useLocation();
  const [page, setPage] = useState(location.state?.page || 1);
  const [pageSize, setPageSize] = useState(location.state?.pageSize || 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    if (!userData) return () => { active = false; };
    IngestedDocumentsService.getDocuments(userData, page, pageSize)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, page, pageSize]);

  return (
    <section className={layout.container} aria-label="Ingested Documents" aria-busy={loading}>
      <Heading>Ingested Documents</Heading>
      <div className={layout.loadingSlot}>
        {loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading ingested documents" />}
      </div>
      <div className={layout.content}>
        {error ? <NotFound /> : data && (
          <table className={style.table}>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">Enrichment status</th>
                <th scope="col">Created at</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.content.map(document => (
                <tr key={document.source_document_id}>
                  <td>{document.filename ?? "—"}</td>
                  <td>{document.status ?? "—"}</td>
                  <td>{document.enrichment?.status ?? "—"}</td>
                  <td><CreatedAt value={document.created_at} /></td>
                  <td className={style.actions}>
                    <Link
                      to={`/intelligence/documents/${encodeURIComponent(document.source_document_id)}/content`}
                      state={{ filename: document.filename, page, pageSize }}
                      aria-label={`View content for ${document.filename || "document"}`}
                      title="View content"
                      className={style.view}
                    ><Eye size={22} aria-hidden="true" /></Link>
                  </td>
                </tr>
              ))}
              {data.content.length === 0 && <tr><td colSpan={5}>No ingested documents found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {!error && data && (
        <div className={layout.pagination}>
          <PaginationGroup activePage={page} numberOfPages={Math.max(1, data.total_pages)} updatePageClicked={setPage} />
          <label className={layout.pageSize}>
            Page size
            <select className="form-select form-select-sm" value={pageSize} onChange={event => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}>
              {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        </div>
      )}
    </section>
  );
};

export default IngestedDocuments;
